import React, { useState, useCallback, useMemo, ReactNode } from 'react';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import { Flex } from '@adsk/alloy-react-box';
import { noop } from '@adsk/alloy-react-helpers';
import Modal from '@adsk/alloy-react-modal';

import EntityPickerModalContent from './EntityPickerModalContent';
import EntityPickerModalTopBar from './EntityPickerModalTopBar';
import useEntitySearch from '../hooks/useEntitySearch';
import { CommonReferencePickerPlugin } from '../types/ReferencePickerPlugin';
import {
  AddEntitiesToCacheProps,
  CreateFormProps,
  EntityWithId,
  PluginQueryFilters,
  SetSelectionHandler,
} from '../types/types';

const FLOATING_PANEL_THRESHOLD = 1140;

type UserPermissionsDataType = {
  canCreate: boolean;
  loading: boolean;
  error: boolean;
  retry: (() => void) | null;
};
interface EntityPickerModalBodyProps<T> {
  sourceEntityType?: string;
  plugin?: CommonReferencePickerPlugin | null;
  loading?: boolean;
  error?: boolean;
  retry?: () => void;
  userPermissionsData: UserPermissionsDataType;
  onChange: ({ entity, checked }: { entity: T; checked?: boolean }) => void;
  setSelection: SetSelectionHandler<T>;
  selectedIds: string[];
  selectedEntities: T[];
  disabledIds?: string[];
  multiple?: boolean;
  isSaving?: boolean;
  addEntitiesToCache?: (option: AddEntitiesToCacheProps) => void;
  createFormProps?: CreateFormProps;
  dataViewProps?: Record<string, unknown>;
  onCreateSuccess?: (entities: T | T[], selectedEntities?: T[]) => void;
  createPanelOpen?: boolean;
  setCreatePanelOpen?: (open: boolean) => void;
  onUpdate?: (entity: T) => void;
  infoMessage?: ReactNode;
  isPluginDisabledInEntitlements?: boolean;
}

const EntityPickerModalBody = <T extends EntityWithId>({
  sourceEntityType,
  plugin,
  loading: loadingProp,
  error: errorProp,
  retry: retryProp,
  userPermissionsData,
  onChange,
  setSelection,
  isSaving,
  addEntitiesToCache,
  selectedIds,
  selectedEntities: selectedEntitiesProp,
  disabledIds,
  multiple,
  createFormProps,
  dataViewProps,
  onCreateSuccess: onCreateSuccessProp,
  onUpdate,
  createPanelOpen,
  setCreatePanelOpen,
  infoMessage,
  isPluginDisabledInEntitlements,
}: EntityPickerModalBodyProps<T>) => {
  const {
    entities: searchedEntities,
    loading: loadingEntities,
    error: errorLoadingEntities,
    onTryAgain: reloadEntities,
    onLoadMore,
    filters,
    setFilters,
    sort,
    setSort,
    clearCache,
  } = useEntitySearch<T>({ plugin, addEntitiesToCache });

  const {
    showSearchBox,
    FiltersPanel,
    CreateNewEntityPanel,
    TopBarActions,
    modalProps,
    onSearchExpressionChange,
    CustomTopBar,
  } = plugin ?? {};

  const [localCreatedEntitiesCache, setLocalCreatedEntitiesCache] = useState<
    T[]
  >([]);
  const [shouldResetEntitySearchCache, setShouldResetEntitySearchCache] =
    useState(false);

  const setFiltersAndClearCache = useCallback(
    (...params: [PluginQueryFilters]) => {
      if (shouldResetEntitySearchCache) {
        clearCache();
        setShouldResetEntitySearchCache(false);
      }

      setFilters(...params);
      setLocalCreatedEntitiesCache([]);
    },
    [clearCache, setFilters, shouldResetEntitySearchCache],
  );

  const TopBar = CustomTopBar || EntityPickerModalTopBar;

  const width = modalProps?.width;
  const size = modalProps?.size || Modal.SIZES.EXTRA_LARGE;
  const floatingPanel = width
    ? typeof width === 'number' && width < FLOATING_PANEL_THRESHOLD
    : Modal.SIZES.EXTRA_LARGE !== size;

  const [filtersOpen, setFiltersOpen] = useState(false);

  const closeFiltersPanel = useCallback(() => setFiltersOpen(false), []);
  const openFiltersPanel = useCallback(() => {
    setFiltersOpen(true);
    setCreatePanelOpen?.(false);
  }, [setCreatePanelOpen]);
  const toggleFiltersPanel = useCallback(() => {
    setFiltersOpen((v) => !v);
    setCreatePanelOpen?.(false);
  }, [setCreatePanelOpen]);
  const closeCreatePanel = useCallback(
    () => setCreatePanelOpen?.(false),
    [setCreatePanelOpen],
  );
  const openCreatePanel = useCallback(() => {
    setCreatePanelOpen?.(true);
    setFiltersOpen(false);
  }, [setCreatePanelOpen]);

  const onCreateSuccess = useCallback(
    (entities: T | T[], options?: { selectedEntities: T[] }) => {
      const selectedEntities =
        options?.selectedEntities || selectedEntitiesProp || [];
      if (plugin?.keepEntityPickerModalOpenAfterCreate) {
        const entitiesArray = Array.isArray(entities) ? entities : [entities];

        setLocalCreatedEntitiesCache([
          ...entitiesArray,
          ...localCreatedEntitiesCache,
        ]);

        setSelection(
          multiple
            ? [...entitiesArray, ...selectedEntities]
            : entitiesArray.slice(0, 1),
        );

        setShouldResetEntitySearchCache(true);
      }

      setCreatePanelOpen?.(false);
      setFiltersOpen(false);
      onCreateSuccessProp?.(entities, selectedEntities);
    },
    [
      localCreatedEntitiesCache,
      multiple,
      selectedEntitiesProp,
      onCreateSuccessProp,
      plugin?.keepEntityPickerModalOpenAfterCreate,
      setCreatePanelOpen,
      setSelection,
    ],
  );

  const projectId = useCurrentProjectId() || '';

  // When clearing filters we want to keep search box state.
  const ignoreFilterKeys = showSearchBox ? ['searchExpression'] : [];

  const clearFilters = () =>
    setFiltersAndClearCache(
      ignoreFilterKeys.reduce<Record<string, unknown>>((acc, k) => {
        acc[k] = filters[k];
        return acc;
      }, {}),
    );

  const filtersCount = Object.keys(filters).filter(
    (k) => !ignoreFilterKeys.includes(k),
  ).length;

  // We can't show entities before getting all data needed, so we could mark already linked ones.
  const entities = useMemo(
    () =>
      loadingProp || errorProp
        ? []
        : [...localCreatedEntitiesCache, ...searchedEntities],
    [errorProp, loadingProp, localCreatedEntitiesCache, searchedEntities],
  );

  const loading = loadingProp || loadingEntities || userPermissionsData.loading;
  const error =
    !!errorProp || errorLoadingEntities || !!userPermissionsData.error;
  const onTryAgain = error
    ? () => {
        if (errorProp && retryProp) {
          retryProp();
        }
        if (userPermissionsData.error && userPermissionsData.retry) {
          userPermissionsData.retry();
        }
        if (errorLoadingEntities) reloadEntities?.();
      }
    : undefined;

  const canCreate = userPermissionsData?.canCreate;
  const renderCreatePanel = canCreate && CreateNewEntityPanel;
  const disabled = isSaving || createPanelOpen;

  const filtersAvailable = !!FiltersPanel;

  return (
    <Flex position="relative" maxHeight="100%" flex="1">
      <Flex flexDirection="column" flex="1" padding="16px" minWidth="0">
        {(showSearchBox ||
          canCreate ||
          FiltersPanel ||
          TopBarActions ||
          CustomTopBar) && (
          <TopBar
            searchExpression={filters?.searchExpression}
            onSearchChange={(searchExpression) => {
              const newFilters = { ...filters };
              if (searchExpression) {
                newFilters.searchExpression = searchExpression;
              } else {
                delete newFilters.searchExpression;
              }
              setFiltersAndClearCache(newFilters);

              if (onSearchExpressionChange)
                onSearchExpressionChange(searchExpression);
            }}
            showSearchBox={!!showSearchBox}
            canCreate={canCreate}
            onCreateClick={openCreatePanel}
            plugin={plugin}
            disabled={!!disabled}
            multiple={multiple}
            filtersAvailable={filtersAvailable}
            filtersOpen={filtersOpen}
            filters={filters}
            toggleFiltersPanel={toggleFiltersPanel}
            clearFilters={clearFilters}
            filtersCount={filtersCount}
            setFilters={setFiltersAndClearCache}
            infoMessage={infoMessage}
            createFormProps={createFormProps}
            isPluginDisabledInEntitlements={isPluginDisabledInEntitlements}
          />
        )}
        {plugin && (
          <EntityPickerModalContent<T>
            entities={entities}
            loading={loading}
            error={error}
            onTryAgain={onTryAgain || noop}
            setSelection={setSelection}
            onChange={onChange}
            disabled={disabled}
            selectedIds={selectedIds}
            disabledIds={disabledIds}
            multiple={multiple}
            onLoadMore={onLoadMore}
            plugin={plugin}
            filters={filters}
            addEntitiesToCache={addEntitiesToCache}
            canCreate={canCreate}
            onCreateClick={openCreatePanel}
            setFilters={setFiltersAndClearCache}
            openFiltersPanel={openFiltersPanel}
            dataViewProps={dataViewProps}
            onUpdate={onUpdate}
            sort={sort}
            setSort={setSort}
            isPluginDisabledInEntitlements={isPluginDisabledInEntitlements}
          />
        )}
      </Flex>
      {filtersAvailable && FiltersPanel && (
        <FiltersPanel
          open={filtersOpen}
          filters={filters}
          setFilters={setFiltersAndClearCache}
          onClose={closeFiltersPanel}
          floatingPanel={floatingPanel}
          clearFilters={clearFilters}
          hasFilters={!!filtersCount}
        />
      )}
      {renderCreatePanel && (
        <CreateNewEntityPanel
          {...createFormProps}
          entities={entities}
          projectId={projectId}
          open={!!createPanelOpen}
          sourceEntityType={sourceEntityType}
          onClose={closeCreatePanel}
          floatingPanel={floatingPanel}
          onCreateSuccess={onCreateSuccess}
        />
      )}
    </Flex>
  );
};

export default EntityPickerModalBody;
